import { useState, useEffect, useCallback } from "react";
import { Send, Users, Plus, X, AlertCircle, CheckCircle, Loader2, Info, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import API from "@/lib/api";
import { toast } from "sonner";
import { CUSTOMER } from "@/constants/testIds";

function getSmsInfo(message) {
  const GSM7 = /^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./0-9:;<=>?¡A-ZÄÖÑÜ§¿a-zäöñüà\^{}\\\[~\]|€]*$/;
  const isGsm = GSM7.test(message);
  const len = isGsm ? [...message].reduce((a, c) => a + ("^{}\\[~]|€".includes(c) ? 2 : 1), 0) : message.length;
  const sl = isGsm ? 160 : 70;
  const ml = isGsm ? 153 : 67;
  const segs = len === 0 ? 0 : len <= sl ? 1 : Math.ceil(len / ml);
  const rem = segs === 0 ? sl : segs === 1 ? sl - len : ml - (len % ml || ml);
  return { encoding: isGsm ? "GSM-7" : "Unicode", charCount: len, segments: segs, charsRemaining: rem };
}

export default function ComposeSMS() {
  const [senderIds, setSenderIds] = useState([]);
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState({ sender_id: "", message: "", forcednd: 0 });
  const [recipients, setRecipients] = useState([]);
  const [inputPhone, setInputPhone] = useState("");
  const [wallet, setWallet] = useState(null);
  const [creditsPerSegment, setCreditsPerSegment] = useState(1);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [result, setResult] = useState(null);
  const smsInfo = getSmsInfo(form.message);
  const totalCredits = smsInfo.segments * recipients.length * creditsPerSegment;

  const loadSenderIds = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await API.get("/sender-ids");
      const approved = data.sender_ids?.filter((s) => s.status === "approved") || [];
      setSenderIds(approved);
      if (!silent && approved.length > 0) toast.success(`${approved.length} approved sender ID(s) loaded`);
      else if (!silent) toast.info("No approved sender IDs yet. Request one in the Sender IDs section.");
    } catch {}
    finally { setRefreshing(false); }
  }, []);

  useEffect(() => {
    Promise.all([
      loadSenderIds(true),
      API.get("/contacts/groups").then(({ data }) => setGroups(data.groups || [])),
      API.get("/wallet/balance").then(({ data }) => setWallet(data)),
      API.get("/sms/cost-info").then(({ data }) => setCreditsPerSegment(data.credits_per_segment || 1)),
    ]).catch(() => {});
  }, []);

  const addPhone = (e) => {
    if ((e.key === "Enter" || e.key === ",") && inputPhone.trim()) {
      e.preventDefault();
      const phones = inputPhone.split(",").map((p) => p.trim()).filter(Boolean);
      setRecipients((prev) => [...new Set([...prev, ...phones])]);
      setInputPhone("");
    }
  };

  const addGroup = async (gid) => {
    try {
      const { data } = await API.get(`/contacts?group_id=${gid}&limit=500`);
      const phones = (data.contacts || []).map((c) => c.phone_normalized || c.phone).filter(Boolean);
      setRecipients((prev) => [...new Set([...prev, ...phones])]);
      toast.success(`Added ${phones.length} contacts from group`);
    } catch { toast.error("Failed to load group contacts"); }
  };

  const handleSend = async () => {
    setLoading(true);
    try {
      const { data } = await API.post("/sms/send", {
        sender_id: form.sender_id,
        recipients,
        message: form.message,
        forcednd: form.forcednd,
      });
      setResult(data);
      setConfirmModal(false);
      toast.success(`${data.sent} message(s) sent successfully!`);
      setForm({ ...form, message: "" });
      setRecipients([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setLoading(false);
    }
  };

  const canSend = form.sender_id && form.message.trim() && recipients.length > 0;
  const insufficientBalance = wallet && totalCredits > 0 && wallet.balance < totalCredits;

  return (
    <div data-testid={CUSTOMER.smsForm} className="space-y-5 max-w-3xl">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Compose SMS</h1>
        <p className="text-sm text-slate-500 mt-1">Send SMS to individuals or groups</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2 space-y-5">
          {/* Sender ID */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-slate-700 text-sm font-medium">Sender ID *</Label>
              <button onClick={() => loadSenderIds(false)} disabled={refreshing} className="text-xs text-[#0c1e41] hover:text-[#091629] flex items-center gap-1 transition-colors disabled:opacity-50">
                <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} /> Refresh
              </button>
            </div>
            {senderIds.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-700 flex items-center gap-2">
                <AlertCircle size={14} /> No approved Sender IDs. Request one in Sender IDs section, or click Refresh if recently approved.
              </div>
            ) : (
              <select
                data-testid={CUSTOMER.smsSenderId}
                value={form.sender_id}
                onChange={(e) => setForm({ ...form, sender_id: e.target.value })}
                className="w-full h-10 bg-white border border-slate-300 text-slate-900 rounded-md px-3 text-sm focus:outline-none focus:border-[#0c1e41]"
              >
                <option value="">Select Sender ID</option>
                {senderIds.map((s) => <option key={s._id} value={s.sender_id}>{s.sender_id}</option>)}
              </select>
            )}
          </div>

          {/* Recipients */}
          <div className="space-y-2">
            <Label className="text-slate-700 text-sm font-medium">Recipients *</Label>
            <div className="bg-white border border-slate-300 rounded-lg p-3 min-h-[80px] focus-within:border-[#0c1e41] transition-colors shadow-sm">
              <div className="flex flex-wrap gap-2 mb-2">
                {recipients.map((r) => (
                  <Badge key={r} variant="outline" className="border-[#c0cfe8] text-[#0c1e41] bg-[#eef1f8] text-xs pr-1">
                    {r}
                    <button onClick={() => setRecipients(recipients.filter((p) => p !== r))} className="ml-1.5 hover:text-red-500">
                      <X size={10} />
                    </button>
                  </Badge>
                ))}
              </div>
              <input
                data-testid={CUSTOMER.smsRecipient}
                value={inputPhone}
                onChange={(e) => setInputPhone(e.target.value)}
                onKeyDown={addPhone}
                onBlur={() => { if (inputPhone.trim()) { setRecipients((p) => [...new Set([...p, ...inputPhone.split(",").map(s=>s.trim()).filter(Boolean)])]); setInputPhone(""); } }}
                placeholder="Type phone number and press Enter (e.g. 08012345678)"
                className="bg-transparent text-sm text-slate-900 placeholder:text-slate-400 outline-none w-full"
              />
            </div>
            {groups.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="text-xs text-slate-500">Add from group:</span>
                {groups.slice(0, 5).map((g) => (
                  <button key={g._id} onClick={() => addGroup(g._id)} className="text-xs text-[#0c1e41] hover:text-[#0c1e41] bg-[#eef1f8] hover:bg-[#dce4f0] border border-[#c0cfe8] px-2 py-1 rounded-md flex items-center gap-1 transition-colors">
                    <Plus size={10} /> {g.name} ({g.contact_count})
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label className="text-slate-700 text-sm font-medium">Message *</Label>
            <Textarea
              data-testid={CUSTOMER.smsMessage}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Type your message here..."
              rows={5}
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 resize-none focus:border-[#0c1e41] text-sm shadow-sm"
            />
            {/* Segment Info */}
            <div data-testid={CUSTOMER.smsSegmentInfo} className="flex items-center justify-between text-xs font-mono text-slate-500 bg-slate-50 border border-slate-200 px-3 py-2 rounded-lg">
              <span>{smsInfo.charCount} / {smsInfo.encoding === "GSM-7" ? "160" : "70"} chars</span>
              <span>Segments: <strong className={smsInfo.segments > 1 ? "text-amber-700" : "text-emerald-700"}>{smsInfo.segments}</strong></span>
              <span className="text-slate-400">{smsInfo.encoding}</span>
              <span>{smsInfo.charsRemaining} remaining</span>
            </div>
          </div>

          {/* DND */}
          <div className="flex items-center gap-3">
            <input type="checkbox" id="dnd" checked={form.forcednd === 1} onChange={(e) => setForm({ ...form, forcednd: e.target.checked ? 1 : 0 })} className="w-4 h-4 accent-[#0c1e41]" />
            <label htmlFor="dnd" className="text-sm text-slate-600">Force send to DND numbers</label>
          </div>

          {insufficientBalance && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-xs text-red-700 flex items-center gap-2">
              <AlertCircle size={14} /> Insufficient credits. Need {totalCredits} credits, have {wallet?.balance || 0}.
            </div>
          )}

          <Button
            data-testid={CUSTOMER.smsSendButton}
            onClick={() => setConfirmModal(true)}
            disabled={!canSend || insufficientBalance}
            className="w-full bg-[#0c1e41] hover:bg-[#091629] text-white h-11 font-semibold shadow-sm"
          >
            <Send size={16} className="mr-2" /> Review & Send
          </Button>
        </div>

        {/* Info Panel */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 shadow-sm">
            <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Info size={14} className="text-[#0c1e41]" /> Send Summary</h4>
            <div className="space-y-2">
              {[
                { label: "Recipients", value: recipients.length },
                { label: "Segments/msg", value: smsInfo.segments || 0 },
                { label: "Credits/segment", value: creditsPerSegment },
                { label: "Total Credits", value: totalCredits },
                { label: "Available Credits", value: wallet?.balance || 0 },
                { label: "After Send", value: (wallet?.balance || 0) - totalCredits },
              ].map((row) => (
                <div key={row.label} className="flex justify-between text-xs border-b border-slate-100 pb-1.5 last:border-0">
                  <span className="text-slate-500">{row.label}</span>
                  <span className={`font-mono font-semibold ${row.label === "After Send" && row.value < 0 ? "text-red-700" : "text-slate-900"}`}>
                    {row.value.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={16} className="text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-700">Sent!</span>
              </div>
              <p className="text-xs text-slate-600">{result.sent} message(s) • {result.credits_used} credits used</p>
              {!result.provider_configured && <p className="text-xs text-amber-700 mt-1">Provider not configured — messages logged for testing.</p>}
            </div>
          )}
        </div>
      </div>

      {/* Confirm Modal */}
      <Dialog open={confirmModal} onOpenChange={setConfirmModal}>
        <DialogContent className="bg-white border-slate-200 max-w-sm">
          <DialogHeader><DialogTitle className="text-slate-900">Confirm Send</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {[
              { label: "Sender ID", value: form.sender_id },
              { label: "Recipients", value: recipients.length },
              { label: "Segments", value: smsInfo.segments },
              { label: "Total Credits", value: totalCredits },
              { label: "Credits After", value: (wallet?.balance || 0) - totalCredits },
            ].map((r) => (
              <div key={r.label} className="flex justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                <span className="text-slate-500">{r.label}</span>
                <span className={`font-mono font-semibold ${r.label === "Credits After" && r.value < 0 ? "text-red-700" : "text-slate-900"}`}>{r.value?.toLocaleString()}</span>
              </div>
            ))}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 line-clamp-3">{form.message}</div>
            <Button onClick={handleSend} disabled={loading} className="w-full bg-[#0c1e41] hover:bg-[#091629] shadow-sm">
              {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
              Confirm & Send
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
